import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  ROUTED_PULSE_TTL_MS,
  type RouteDecision,
  type WorkspaceTab,
  WORKSPACE_TABS,
} from "@ando/core/domain";
import { zustandStorage } from "@/lib/storage";

/**
 * Workspace router store — what tab the canvas is showing and *who put it
 * there* (the user manually vs. Ando routing the conversation).
 *
 * `routedBy: 'ando'` drives the orange "routed-pulse" animation defined in
 * the Tailwind preset — when Ando navigates the canvas in response to a
 * conversation move, the active rail glows for ~700ms so the user *sees* the
 * routing happen. The pulse is one-shot; UI clears `routedBy` back to
 * `'manual'` after the animation finishes (or on the next manual click).
 *
 * `routedByAndoUntil` is the *absolute timestamp* (ms since epoch) until
 * which the auto-routing visual cue should remain visible (pulse + "Aperto
 * da Ando" label). UI consumers compare against `Date.now()` — TTL keeps
 * the pulse state self-cleaning even if the user navigates away mid-window.
 *
 * `previousTab` tracks the last destination before the most recent route
 * so the Undo chip can flip back without consulting routerHistoryStore
 * (still recorded there for audit).
 *
 * Persistence: only `currentTab` survives reloads — `routedBy` /
 * `routedByAndoUntil` / `previousTab` are always fresh on boot, otherwise
 * the user would land on a routed-pulse state with no triggering
 * conversation.
 */

// Re-export the centralized type + constant so existing call-sites keep
// their import path (`from "../state/workspaceStore"`) but the source of
// truth lives in @ando/core/domain (cross-package DRY).
export type { WorkspaceTab };
export { WORKSPACE_TABS };

export type RoutedBy = "manual" | "ando";

/** TTL for the auto-routing visual cue, in ms. Aligned with v18 mockup. */
export const ROUTED_BY_ANDO_TTL_MS = ROUTED_PULSE_TTL_MS;

/** Max depth of the pivot-history stack (Phase 4.B). N=3 per task brief. */
export const PIVOT_HISTORY_DEPTH = 3;

/**
 * Snapshot of a tab transition driven by Ando — pushed onto pivotHistory
 * so revertLastPivot can step back to the previous workspace state
 * (tab + sub-tab) without re-consulting routerHistoryStore (still recorded
 * there for audit). Stored as plain data (no functions / refs) so the
 * Zustand persist middleware can serialise the slice cleanly.
 */
export interface PivotHistoryEntry {
  /** Tab the user was on *before* the pivot. */
  fromTab: WorkspaceTab;
  /** Tab the pivot navigated *to*. */
  toTab: WorkspaceTab;
  /** Epoch ms when the pivot fired. */
  timestamp: number;
  /** Decision payload — kept for replay + dev tools. */
  decision: RouteDecision;
}

interface WorkspaceState {
  currentTab: WorkspaceTab;
  routedBy: RoutedBy;
  /** Tab that received the most recent Ando route (for cross-tab cue cleanup). */
  routedTab: WorkspaceTab | null;
  /** Epoch ms — the pulse + "Aperto da Ando" label remain visible until this. */
  routedByAndoUntil: number | null;
  /** LIFO stack of recent Ando pivots (max PIVOT_HISTORY_DEPTH). */
  pivotHistory: PivotHistoryEntry[];
  /**
   * Per-tab selected sub-tab id. Persisted across reloads so the user's
   * last filter choice survives — Ando's auto-routing also writes here
   * via ``setSubTab(tab, sub, "ando")`` so the chat intent classifier
   * can land on the right chip without re-navigating.
   */
  subTabByTab: Partial<Record<WorkspaceTab, string>>;
  /**
   * User-customised order of the top working-bar tabs. Empty = use the
   * default ``WORKSPACE_TAB_DEFS`` order; once the user drags (or keyboard-
   * moves) a tab the full resolved order is stored. Persisted so the layout
   * sticks per device. Unknown/removed ids are ignored and newly-added tabs
   * append at the end — see ``reorderTabDefs`` in ``WorkspaceTabs``.
   */
  tabOrder: WorkspaceTab[];
  setTab: (tab: WorkspaceTab, source?: "manual" | "ando") => void;
  /**
   * Set the sub-tab id for a given top tab. ``source="ando"`` keeps the
   * routed-by-Ando pulse alive on the parent tab so the user sees both
   * cues at once (parent tab pulses + chip is selected).
   */
  setSubTab: (tab: WorkspaceTab, subTab: string, source?: "manual" | "ando") => void;
  /** Replace the working-bar tab order (drag-to-reorder + keyboard move). */
  setTabOrder: (order: WorkspaceTab[]) => void;
  routeFromAndo: (tab: WorkspaceTab) => void;
  /**
   * Route to ``(tab, subTab)`` in a single store update. Used by the
   * chat intent classifier: ``"fatture in scadenza"`` →
   * ``routeWithSubTab("finance", "sdi")``.
   */
  routeWithSubTab: (tab: WorkspaceTab, subTab: string) => void;
  clearRoutedBy: () => void;
  /** Push a pivot onto the history stack (LIFO, capped at depth). */
  pushPivot: (entry: PivotHistoryEntry) => void;
  /** Pop the top pivot off the stack and return it (or null when empty). */
  popPivot: () => PivotHistoryEntry | null;
  /** Clear the pivot stack (e.g. on conversation reset). */
  clearPivotHistory: () => void;
}

const STORAGE_KEY = "ando.workspace";

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      currentTab: "calendar",
      routedBy: "manual",
      routedTab: null,
      routedByAndoUntil: null,
      pivotHistory: [],
      subTabByTab: {},
      tabOrder: [],
      setTab: (tab, source = "manual") =>
        set(
          source === "ando"
            ? {
                currentTab: tab,
                routedBy: "ando",
                routedTab: tab,
                routedByAndoUntil: Date.now() + ROUTED_BY_ANDO_TTL_MS,
              }
            : {
                currentTab: tab,
                routedBy: "manual",
                routedTab: null,
                routedByAndoUntil: null,
              },
        ),
      setSubTab: (tab, subTab, source = "manual") =>
        set((state) => {
          const nextMap = { ...state.subTabByTab, [tab]: subTab };
          if (source === "ando") {
            return {
              subTabByTab: nextMap,
              routedBy: "ando",
              routedTab: tab,
              routedByAndoUntil: Date.now() + ROUTED_BY_ANDO_TTL_MS,
            };
          }
          return { subTabByTab: nextMap };
        }),
      setTabOrder: (order) => set({ tabOrder: order }),
      routeFromAndo: (tab) =>
        set({
          currentTab: tab,
          routedBy: "ando",
          routedTab: tab,
          routedByAndoUntil: Date.now() + ROUTED_BY_ANDO_TTL_MS,
        }),
      routeWithSubTab: (tab, subTab) =>
        set((state) => ({
          currentTab: tab,
          subTabByTab: { ...state.subTabByTab, [tab]: subTab },
          routedBy: "ando",
          routedTab: tab,
          routedByAndoUntil: Date.now() + ROUTED_BY_ANDO_TTL_MS,
        })),
      clearRoutedBy: () =>
        set({ routedBy: "manual", routedTab: null, routedByAndoUntil: null }),
      pushPivot: (entry) =>
        set((state) => {
          const next = [...state.pivotHistory, entry];
          // Cap the stack at PIVOT_HISTORY_DEPTH; oldest entries drop off
          // the bottom (LIFO ordering: pop reads .at(-1)).
          const trimmed =
            next.length > PIVOT_HISTORY_DEPTH
              ? next.slice(next.length - PIVOT_HISTORY_DEPTH)
              : next;
          return { pivotHistory: trimmed };
        }),
      popPivot: () => {
        const stack = get().pivotHistory;
        const last = stack.at(-1) ?? null;
        if (last !== null) {
          set({ pivotHistory: stack.slice(0, -1) });
        }
        return last;
      },
      clearPivotHistory: () => set({ pivotHistory: [] }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => zustandStorage),
      // Persist `currentTab` + `subTabByTab` — the user's last sub-tab
      // pick survives a reload (the filter feels sticky). The orange
      // pulse, pivot stack, and routed cue are NEVER persisted (a fresh
      // session starts with no undo affordance).
      partialize: (state) => ({
        currentTab: state.currentTab,
        subTabByTab: state.subTabByTab,
        tabOrder: state.tabOrder,
      }),
    },
  ),
);

// ── Type-safe selectors (subscribe to slices to avoid re-renders) ─────────

export const selectCurrentTab = (state: WorkspaceState): WorkspaceTab =>
  state.currentTab;

export const selectActiveTab = selectCurrentTab;

export const selectRoutedBy = (state: WorkspaceState): RoutedBy =>
  state.routedBy;

export const selectRoutedTab = (state: WorkspaceState): WorkspaceTab | null =>
  state.routedTab;

export const selectRoutedByAndoUntil = (
  state: WorkspaceState,
): number | null => state.routedByAndoUntil;

export const selectSetTab = (
  state: WorkspaceState,
): WorkspaceState["setTab"] => state.setTab;

export const selectSetSubTab = (
  state: WorkspaceState,
): WorkspaceState["setSubTab"] => state.setSubTab;

export const selectTabOrder = (state: WorkspaceState): WorkspaceTab[] =>
  state.tabOrder;

export const selectSetTabOrder = (
  state: WorkspaceState,
): WorkspaceState["setTabOrder"] => state.setTabOrder;

/** Legacy sub-tab IDs → the current canonical value. Surfaced inside
 *  ``selectSubTabFor`` so deep-links + persisted state from before the
 *  CAD Big Bang (Jun 12, 2026 — "drawings" → 2D / 3D / assemblies split)
 *  resolve to the new IDs without flushing the user's preferences. */
const _LEGACY_SUB_TAB_MIGRATIONS: Readonly<Record<string, string>> = {
  drawings: "drawings_2d",
};

export const selectSubTabFor =
  (tab: WorkspaceTab) =>
  (state: WorkspaceState): string | undefined => {
    const stored = state.subTabByTab[tab];
    if (stored === undefined) return undefined;
    return _LEGACY_SUB_TAB_MIGRATIONS[stored] ?? stored;
  };

export const selectRouteFromAndo = (
  state: WorkspaceState,
): WorkspaceState["routeFromAndo"] => state.routeFromAndo;

export const selectRouteWithSubTab = (
  state: WorkspaceState,
): WorkspaceState["routeWithSubTab"] => state.routeWithSubTab;

export const selectClearRoutedBy = (
  state: WorkspaceState,
): WorkspaceState["clearRoutedBy"] => state.clearRoutedBy;

export const selectPivotHistory = (state: WorkspaceState): PivotHistoryEntry[] =>
  state.pivotHistory;

export const selectPushPivot = (
  state: WorkspaceState,
): WorkspaceState["pushPivot"] => state.pushPivot;

export const selectPopPivot = (
  state: WorkspaceState,
): WorkspaceState["popPivot"] => state.popPivot;

export const selectClearPivotHistory = (
  state: WorkspaceState,
): WorkspaceState["clearPivotHistory"] => state.clearPivotHistory;

/**
 * Curried selector: is the *given* tab currently the Ando-routed one with
 * an unexpired TTL? Pass through `useWorkspaceStore(selectIsRoutedByAndo(tab))`.
 *
 * Note: returns a snapshot — to stay reactive across the TTL window,
 * components wrap this in `useRoutedByAndoPulse(tabId)` (auto-rerender on
 * expiry).
 */
export const selectIsRoutedByAndo =
  (tab: WorkspaceTab) =>
  (state: WorkspaceState): boolean =>
    state.routedTab === tab &&
    state.routedByAndoUntil !== null &&
    state.routedByAndoUntil > Date.now();
